import React, { useState, useEffect } from 'react';
import { fetchWeekendAnalytics, fetchDailyAnalytics, fetchInsights } from '../api/client';
import { relevantWeekend, israelToday, israelDow, shiftStr, fmtShortDate, fmtShortRange } from '../components/DateRangePicker';
import Poster from '../components/Poster';
import WednesdayChip from '../components/WednesdayChip';
import Podium, { RankNum, occColor, useBoxWidth } from '../components/Podium';
import { useAuth } from '../context/AuthContext';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * "מה חדש" — the weekend is the story (§12.10):
 *  - main column: the weekend's leaders, its total and change in the card's
 *    own header, the top three as a podium of posters in medal colours, then
 *    a table from #1 with occupancy in colour;
 *  - side column: today's leaders and the coming weekend (Tuesday–Wednesday
 *    only), each with its own total on the left of the title, like the
 *    weekend card, but no change — both are still selling —
 *    and the presales of films not yet open as small podium tiles.
 * Content is capped at PAGE_MAX so a wide screen never stretches a card into
 * an empty strip. On a phone the same cards stack in that order.
 */

const PAGE_MAX = 1500;
const fmt = (v) => (v || 0).toLocaleString();
const card = { background: 'var(--surf)', borderRadius: 16, boxShadow: 'var(--shadow)', padding: 20, minWidth: 0 };
const K_DAY = { t: 'day_tickets', s: 'day_screenings', p: 'prev_tickets' };
const K_WKD = { t: 'weekend_tickets', s: 'weekend_screenings', p: 'prev_weekend_tickets' };
const topRows = (data, k, n) => (data?.by_movie || []).filter(m => (m[k.t] || 0) > 0).slice(0, n);

// "<title> - <dates>". On a phone whatever sits beside the title drops under
// it, and the dates never break inside themselves.
function Hd({ title, dates, tag, mob, size, aside }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px 16px', flexWrap: 'wrap', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' }}>
        <div style={{ fontSize: size || (mob ? 18 : 20), fontWeight: 800, color: 'var(--txt)', lineHeight: 1.3 }}>
          {title}
          {dates && <> - <span dir="ltr" style={{ unicodeBidi: 'isolate', whiteSpace: 'nowrap' }}>{dates}</span></>}
        </div>
        {tag && (
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--presale)', background: 'var(--presale-bg)',
                         padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{tag}</span>
        )}
      </div>
      {aside}
    </div>
  );
}

function NewBadge({ size = 11 }) {
  return <span style={{ fontSize: size, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)',
                        padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>חדש</span>;
}

// ↑12% / ↓8% against last week, or "חדש" for a film with nothing to compare to.
function Change({ curr, prev, size = 11 }) {
  if (!prev) return <NewBadge size={size} />;
  const pct = Math.round((curr - prev) * 100 / prev);
  const up = pct >= 0;
  return (
    <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: size, fontWeight: 700, whiteSpace: 'nowrap',
                             color: up ? 'var(--ok)' : 'var(--danger)' }}>
      {up ? '↑' : '↓'}{Math.abs(pct)}%
    </span>
  );
}

// Hebrew name; under it the Wednesday chip and the English name.
function Title({ mv }) {
  const en = mv.title_en && mv.title_en !== (mv.title_he || mv.title) ? mv.title_en : null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {mv.title_he || mv.title}
      </div>
      {(mv.wednesday_preview || en) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, marginTop: 1 }}>
          {mv.wednesday_preview && <WednesdayChip tickets={mv.preview_tickets} />}
          {en && <div style={{ fontSize: 10.5, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{en}</div>}
        </div>
      )}
    </div>
  );
}

// Places 1-3 in Olympic medal colours (components/Podium.js).
const rank = (i) => <RankNum i={i} />;

// Occupancy in the weekend table: under 10% red, under 18% amber, 18% and up green.
const Occ = ({ value }) => {
  const p = Math.round(value || 0);
  return <span style={{ color: occColor(p), fontWeight: 700 }}>{p}%</span>;
};
const rowStyle = (last) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer',
                              borderBottom: last ? 'none' : '1px solid var(--brd)' });

// A row with tickets, screenings and the film's total stacked beside its name.
// `occ` adds the coloured occupancy to the screenings line (the weekend table on a phone).
function StackRow({ mv, i, k, onClick, last, occ }) {
  return (
    <div onClick={() => onClick(mv.movie_id)} style={rowStyle(last)}>
      {rank(i)}
      <Poster posterUrl={mv.poster_url} genre={mv.genre} letter={(mv.title_he || mv.title || '?')[0]} size="sm" />
      <div style={{ flex: 1, minWidth: 0 }}><Title mv={mv} /></div>
      <div style={{ textAlign: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{fmt(mv[k.t])}</div>
        {mv[k.s] > 0 && (
          <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 1 }}>
            {mv[k.s]} הקרנות{occ && <> · <Occ value={mv.occupancy} /></>}
          </div>
        )}
        {mv.total_tickets > 0 && <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 1 }}>סה״כ {fmt(mv.total_tickets)}</div>}
      </div>
    </div>
  );
}

// Columns for a weekend row when the card is wide enough to hold them.
const COLS = [
  { key: 'tickets', head: 'כרטיסים', w: 74, render: (mv) => <b style={{ color: 'var(--accent)' }}>{fmt(mv.weekend_tickets)}</b> },
  { key: 'change', head: 'שינוי', w: 58, render: (mv) => <Change curr={mv.weekend_tickets} prev={mv.prev_weekend_tickets} /> },
  { key: 'screenings', head: 'הקרנות', w: 58, render: (mv) => fmt(mv.weekend_screenings) },
  { key: 'occ', head: 'תפוסה', w: 52, render: (mv) => <Occ value={mv.occupancy} /> },
  { key: 'total', head: 'סה״כ', w: 80, render: (mv) => fmt(mv.total_tickets) },
];
const cell = (c) => ({ width: c.w, flexShrink: 0, textAlign: 'center', fontSize: 12.5, color: 'var(--muted)',
                       fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' });

function MoreButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      marginTop: 12, padding: '8px 12px', borderRadius: 9, width: '100%', border: '1px solid var(--brd)',
      background: 'var(--surf-alt)', color: 'var(--accent)', fontFamily: 'Heebo, sans-serif', fontSize: 12.5,
      fontWeight: 700, cursor: 'pointer', transition: 'background .12s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--surf-hov)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--surf-alt)'; }}
    >לטבלה המלאה ←</button>
  );
}

const empty = (text) => <div style={{ color: 'var(--faint)', textAlign: 'center', padding: 24, fontSize: 13 }}>{text}</div>;

// The top three as posters, in medal colours: the name, "חדש" for a new film,
// and the weekend's tickets. Every other figure is in the table below them.
function WeekendPodium({ rows, onClick, mob }) {
  return (
    <Podium items={rows} mob={mob} onClick={(mv) => onClick(mv.movie_id)} itemKey={(mv) => mv.movie_id}
      poster={(mv) => <Poster posterUrl={mv.poster_url} genre={mv.genre} letter={(mv.title_he || mv.title || '?')[0]} size="xl" />}
      name={(mv) => mv.title_he || mv.title}
      value={(mv) => mv.weekend_tickets}
      extra={(mv) => (!mv.prev_weekend_tickets || mv.wednesday_preview) && (
        <>
          {!mv.prev_weekend_tickets && <NewBadge />}
          {mv.wednesday_preview && <WednesdayChip tickets={mv.preview_tickets} />}
        </>
      )} />
  );
}

// A card's own total: tickets, then screenings · occupancy, and the change
// when there is a fair one to show (the weekend card only). On a desktop it
// sits on the left of the title in every card — `marginInlineStart: auto`
// keeps it on the left even when a narrow side card wraps it under a long
// title. On a phone it goes under the title, right-aligned.
function CardTotal({ total, screenings, occupancy, prev, mob, small }) {
  return (
    <div style={{ textAlign: mob ? 'right' : 'left', width: mob ? '100%' : 'auto', marginInlineStart: mob ? 0 : 'auto' }}>
      <div style={{ fontSize: mob ? 24 : small ? 26 : 30, fontWeight: 900, color: 'var(--accent)', letterSpacing: -1, lineHeight: 1 }}>
        {fmt(total)}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap',
                    justifyContent: mob ? 'flex-start' : 'flex-end' }}>
        {prev > 0 && <Change curr={total} prev={prev} size={12} />}
        {screenings > 0 && (
          <span style={{ fontSize: 12, color: 'var(--faint)', whiteSpace: 'nowrap' }}>
            {fmt(screenings)} הקרנות · תפוסה {Math.round(occupancy || 0)}%
          </span>
        )}
      </div>
    </div>
  );
}

const totalOf = (data, mob, { change = false, small = false } = {}) => data && (
  <CardTotal total={data.total_tickets} screenings={data.total_screenings} occupancy={data.avg_occupancy}
             prev={change ? data.prev_weekend_tickets : 0} mob={mob} small={small} />
);

function WeekendCard({ data, range, showTotal, showRows, onMovieClick, onMore, mob }) {
  const [ref, width] = useBoxWidth();
  const rows = topRows(data, K_WKD, mob ? 10 : 15);
  const wide = !mob && width >= 640;
  return (
    <div ref={ref} style={{ ...card, padding: mob ? 16 : 22 }}>
      <Hd title="מובילי הסופ״ש" dates={fmtShortRange(range.start, range.end)} mob={mob} size={mob ? 18 : 22}
          aside={showTotal && totalOf(data, mob, { change: true })} />
      {showRows && (rows.length === 0 ? empty('אין נתונים לסופ״ש זה') : (
        <>
          <WeekendPodium rows={rows.slice(0, 3)} onClick={onMovieClick} mob={mob} />
          {/* the table starts at #1: the podium shows the top three, the table their figures */}
          {wide && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0 6px', borderBottom: '1px solid var(--brd)' }}>
              <div style={{ flex: 1 }} />
              {COLS.map(c => <div key={c.key} style={{ ...cell(c), fontSize: 10.5, fontWeight: 700, color: 'var(--faint)' }}>{c.head}</div>)}
            </div>
          )}
          {rows.map((mv, i) => (wide ? (
            <div key={mv.movie_id} onClick={() => onMovieClick(mv.movie_id)} style={rowStyle(false)}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                {rank(i)}
                <Poster posterUrl={mv.poster_url} genre={mv.genre} letter={(mv.title_he || mv.title || '?')[0]} size="sm" />
                <Title mv={mv} />
              </div>
              {COLS.map(c => <div key={c.key} style={cell(c)}>{c.render(mv)}</div>)}
            </div>
          ) : <StackRow key={mv.movie_id} mv={mv} i={i} k={K_WKD} onClick={onMovieClick} occ />))}
          <MoreButton onClick={onMore} />
        </>
      ))}
    </div>
  );
}

function LeadersCard({ title, dates, tag, data, k, n, onMovieClick, onMore, emptyText, mob, aside }) {
  const rows = topRows(data, k, n);
  return (
    <div style={{ ...card, padding: mob ? 16 : 20 }}>
      <Hd title={title} dates={dates} tag={tag} mob={mob} aside={aside} />
      {rows.length === 0
        ? empty(emptyText)
        : rows.map((mv, i) => <StackRow key={mv.movie_id} mv={mv} i={i} k={k} onClick={onMovieClick} last={i === rows.length - 1} />)}
      <MoreButton onClick={onMore} />
    </div>
  );
}

const whenText = (it) => (it.days_until <= 0 ? 'עולה היום' : it.days_until === 1 ? 'עולה מחר' : `בעוד ${it.days_until} ימים`);

// Films not yet open, as a smaller podium: poster, name, tickets sold so far, when it opens.
function PresaleCard({ items, onMovieClick, mob }) {
  const [ref, width] = useBoxWidth();
  const list = (items || []).filter(it => it.movie).slice(0, 4);
  if (list.length === 0) return null;
  const cols = width >= 420 ? Math.min(4, list.length) : Math.min(2, list.length);
  return (
    <div ref={ref} style={{ ...card, padding: mob ? 16 : 20 }}>
      <Hd title="מכירה מוקדמת" mob={mob} />
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 10 }}>
        {list.map(it => (
          <div key={it.movie.id} onClick={() => onMovieClick(it.movie.id)} style={{
            cursor: 'pointer', borderRadius: 12, padding: '10px 6px', minWidth: 0, textAlign: 'center',
            background: 'linear-gradient(160deg, var(--presale-bg), var(--surf-alt) 75%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          }}>
            <Poster posterUrl={it.movie.poster_url} genre={it.movie.genre} letter={(it.movie.title_he || it.movie.title || '?')[0]} size="md" />
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', lineHeight: 1.3, width: '100%',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {it.movie.title_he || it.movie.title}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--presale)', lineHeight: 1 }}>{fmt(it.tickets)}</div>
            <div style={{ fontSize: 10.5, color: 'var(--faint)', whiteSpace: 'nowrap' }}>{whenText(it)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ onMovieClick, onNavigate, refreshNonce }) {
  const { hasPermission } = useAuth();
  const mob = useWindowWidth() < 768;
  const [weekendData, setWeekendData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [nextWeekendData, setNextWeekendData] = useState(null);
  const [presales, setPresales] = useState([]);
  const [loading, setLoading] = useState(true);

  // relevantWeekend() is the weekend in progress (Thu–Sun) or the last one.
  const lastWeekend = relevantWeekend();
  const nextWeekend = { start: shiftStr(lastWeekend.start, 7), end: shiftStr(lastWeekend.end, 7) };
  // Tickets for the coming weekend open on Tuesday; from Thursday it is the
  // current weekend. So its card shows on Tuesday and Wednesday only.
  const dow = israelDow();
  const showNext = dow === 2 || dow === 3;
  const today = israelToday();
  const canPresale = hasPermission('dashboard.insights');

  useEffect(() => {
    const lw = relevantWeekend();
    fetchWeekendAnalytics(lw.start, lw.end).then(wd => {
      setWeekendData(wd);
      setLoading(false);
    }).catch(() => setLoading(false));
    // The other cards load on their own, so one failing never blocks the page
    fetchDailyAnalytics(israelToday()).then(setDailyData).catch(() => setDailyData(null));
    const d = israelDow();
    if (d === 2 || d === 3) {
      fetchWeekendAnalytics(shiftStr(lw.start, 7), shiftStr(lw.end, 7))
        .then(setNextWeekendData).catch(() => setNextWeekendData(null));
    }
    if (canPresale) fetchInsights().then(r => setPresales(r?.items || [])).catch(() => setPresales([]));
  }, [refreshNonce, canPresale]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--faint)' }}>טוען נתונים...</div>;
  }

  const goCharts = (sel) => onNavigate && onNavigate('charts', sel);
  const showLists = hasPermission('dashboard.top_movies');
  const showTotal = hasPermission('dashboard.kpi');

  return (
    <div style={{ maxWidth: PAGE_MAX, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: mob ? 'minmax(0, 1fr)' : 'minmax(0, 2fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        {(showLists || showTotal) && (
          <WeekendCard data={weekendData} range={lastWeekend} showTotal={showTotal} showRows={showLists}
                       onMovieClick={onMovieClick} onMore={() => goCharts({ mode: 'weekend', ...lastWeekend })} mob={mob} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {showLists && (
            <LeadersCard title="מובילי היום" dates={fmtShortDate(today)} data={dailyData} k={K_DAY} n={mob ? 10 : 8}
                         onMovieClick={onMovieClick} onMore={() => goCharts({ mode: 'today', start: today, end: today })}
                         emptyText="אין עדיין נתונים להיום" mob={mob} aside={showTotal && totalOf(dailyData, mob, { small: true })} />
          )}
          {showLists && showNext && (
            <LeadersCard title="מובילי הסופ״ש הקרוב" dates={fmtShortRange(nextWeekend.start, nextWeekend.end)} tag="מכירה מוקדמת"
                         data={nextWeekendData} k={K_WKD} n={5} onMovieClick={onMovieClick}
                         onMore={() => goCharts({ mode: 'weekend', ...nextWeekend })} emptyText="אין עדיין נתוני מכירה מוקדמת" mob={mob}
                         aside={showTotal && totalOf(nextWeekendData, mob, { small: true })} />
          )}
          {canPresale && <PresaleCard items={presales} onMovieClick={onMovieClick} mob={mob} />}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
